import { azureAdProvider } from '@shared/constants';
import resources from '@shared/resources';
import { objectToQueryString } from '@shared/url-utils';
import { signIn, useSession } from 'next-auth/react';
import { useCallback, useRef } from 'react';

import type { SnackbarActions } from './useStore/types';

export type QueryParams = Record<string, QueryParamValue>;

export type QueryParamValue =
  | null
  | number
  | string
  | boolean
  | undefined
  | Array<QueryParamValue>
  | { [key: string]: QueryParamValue };

const UX_TEXT: { [key: string]: string } = {
  PUT: 'updated',
  POST: 'created',
  DELETE: 'deleted',
};

type NotifyUx =
  | boolean
  | {
      error?: string | boolean;
      success?: string | boolean;
      warning?: string | boolean;
    };

export type Download = (url: string, fileName?: string) => Promise<boolean>;

export type FetchData = <T>(
  url: string,
  query?: QueryParams,
  method?: string,
  requestBody?: unknown,
  notifyUx?: NotifyUx,
  contentType?: { 'Content-Type': string },
) => Promise<T>;

export type FetchDataWithError = <T>(
  url: string,
  query?: QueryParams,
  method?: string,
  requestBody?: unknown,
  notifyUx?: NotifyUx,
  contentType?: { 'Content-Type': string },
) => Promise<{ data?: T; error?: any }>;

interface HttpError extends Error {
  status?: number;
  statusText?: string;
}

let refreshSessionPromise: Promise<boolean> | undefined;

const refreshSession = async (): Promise<boolean> => {
  if (!refreshSessionPromise) {
    refreshSessionPromise = fetch('/api/auth/session', {
      credentials: 'include',
    })
      .then(async (res) => {
        const session = (await res.json().catch(() => undefined)) as
          | { error?: unknown; user?: { email?: string } }
          | null
          | undefined;

        return (
          res.ok && !session?.error && typeof session?.user?.email === 'string'
        );
      })
      .finally(() => {
        refreshSessionPromise = undefined;
      });
  }

  return refreshSessionPromise;
};

const createHttpError = (status: number, message: string): HttpError => {
  const error = new Error(message) as HttpError;
  error.status = status;
  return error;
};

export const useDataFetcher = (
  login: () => void,
  snackbar: SnackbarActions,
) => {
  const { data: session } = useSession();
  const signInTriggeredRef = useRef(false);

  const showSnackbar = useCallback(
    (res: Response, data: any, method: string, notifyUx: NotifyUx) => {
      const isError = res.status >= 400;
      const isWarning =
        res.status < 200 || (res.status >= 300 && res.status < 400);
      const isSuccess = res.status >= 200 && res.status < 300;
      let message =
        data?.error?.description ??
        data?.error?.message ??
        "Something isn't quite right";

      if (typeof notifyUx === 'boolean') {
        if (isError) {
          snackbar.error(message);
        } else if (isWarning) {
          snackbar.warning(message, data?.error);
        } else {
          snackbar.success(
            data?.type
              ? `Success, '${data.type}' has been ${UX_TEXT[method]}`
              : 'Success',
          );
        }
        return;
      }

      if (isError && notifyUx.error) {
        if (typeof notifyUx.error === 'string') {
          message = notifyUx.error;
        }
        snackbar.error(message, data?.error);
      }

      if (isWarning && notifyUx.warning) {
        if (typeof notifyUx.warning === 'string') {
          message = notifyUx.warning;
        }
        snackbar.warning(message);
      }

      if (isSuccess && notifyUx.success) {
        let successMessage = 'Success';

        if (typeof notifyUx.success === 'string') {
          successMessage = notifyUx.success;
        } else if (data?.type) {
          successMessage = `Success, '${data.type}' has been ${UX_TEXT[method]}`;
        }

        snackbar.success(successMessage);
      }
    },
    [snackbar],
  );

  const makeRequest = useCallback(
    async (
      url: string,
      method = 'GET',
      body?: unknown,
      headers?: Record<string, string>,
      allowAuthRetry = true,
    ): Promise<Response> => {
      if (!session?.user?.email || session?.error) {
        if (login) {
          login();
        }
        throw createHttpError(401, 'No authentication session available');
      }

      let bodyString: string | undefined;
      if (body) {
        try {
          bodyString = JSON.stringify(body);
        } catch (serializeError) {
          throw createHttpError(
            400,
            `Failed to serialize request body: ${serializeError instanceof Error ? serializeError.message : 'Unknown error'}`,
          );
        }
      }

      const res = await fetch(url, {
        credentials: 'include',
        method,
        ...(headers ? { headers } : {}),
        ...(bodyString ? { body: bodyString as BodyInit } : {}),
      });

      // Handle authentication errors
      if (res.status === 401 && allowAuthRetry) {
        if (await refreshSession()) {
          return makeRequest(url, method, body, headers, false);
        }
      }

      if (res.status === 401) {
        if (!signInTriggeredRef.current) {
          signInTriggeredRef.current = true;
          signIn(azureAdProvider);
        }
        throw createHttpError(401, 'Authentication required');
      }

      return res;
    },
    [login, session?.error, session?.user?.email],
  );

  const handleErrorResponse = useCallback((res: Response, data: any) => {
    const errorMessage =
      data?.error?.description ??
      data?.error?.message ??
      data?.message ??
      res.statusText ??
      'Request failed';

    resources.logger.error(
      `${errorMessage} (status: ${res.status})`,
      'useDataFetcher/handleErrorResponse',
    );

    throw createHttpError(res.status, errorMessage);
  }, []);

  const fetchData = useCallback<FetchData>(
    async (
      url,
      query,
      method = 'GET',
      requestBody,
      notifyUx,
      contentType = { 'Content-Type': 'application/json' },
    ) => {
      try {
        const fullUrl = query ? `${url}?${objectToQueryString(query)}` : url;
        const res = await makeRequest(
          fullUrl,
          method,
          requestBody,
          contentType,
        );

        let data: any;
        try {
          data = await res.json();
        } catch (parseError) {
          throw new Error(
            parseError instanceof Error
              ? `Failed to parse JSON response: ${parseError.message}`
              : 'Failed to parse JSON response',
          );
        }

        if (!res.ok) {
          if (notifyUx) {
            showSnackbar(res, data, method, notifyUx);
          }
          handleErrorResponse(res, data);
        }

        if (notifyUx && res.ok) {
          showSnackbar(res, data, method, notifyUx);
        }

        return data;
      } catch (err) {
        // If already an HttpError, re-throw as-is
        if (err instanceof Error && 'status' in err) {
          throw err;
        }

        // Handle network/parse errors
        const message = err instanceof Error ? err.message : 'Request failed';
        resources.logger.error(message, 'useDataFetcher/fetchData');

        if (notifyUx) {
          snackbar.error(message);
        }

        throw err;
      }
    },
    [makeRequest, handleErrorResponse, showSnackbar, snackbar],
  );

  const fetchDataWithError: FetchDataWithError = useCallback(
    async <T>(
      url: string,
      query?: QueryParams,
      method = 'GET',
      requestBody?: unknown,
      notifyUx?: NotifyUx,
      contentType?: { 'Content-Type': string },
    ): Promise<{ data?: T; error?: any }> => {
      try {
        const data = await fetchData<T>(
          url,
          query,
          method,
          requestBody,
          notifyUx,
          contentType,
        );
        return { data };
      } catch (error) {
        return { error };
      }
    },
    [fetchData],
  );

  const download: Download = useCallback(
    async (url, fileName) => {
      try {
        const res = await makeRequest(url, 'GET', undefined, {
          'Content-Type': 'application/octet-stream',
        });

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          handleErrorResponse(res, data);
          return false;
        }

        const blob = await res.blob();
        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = downloadUrl;
        link.download = fileName || 'download';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(downloadUrl);
        return true;
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Download failed';
        snackbar.error(message);
        resources.logger.error(message, 'useDataFetcher/download');
        return false;
      }
    },
    [makeRequest, handleErrorResponse, snackbar],
  );

  return { download, fetchData, fetchDataWithError };
};
